import styles from './InsuranceTool.module.scss';
import * as React from 'react';
import moment from 'moment-timezone';
import { ComboBox, DatePicker, Pivot, PivotItem, PrimaryButton, SelectableOptionMenuItemType, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { changeCoverage, Patient } from '../../../api';
import { ApiResponse } from 'apisauce';
import { HealAPI } from '../../../healApi';
import { AccountResponse } from '../../../api/accounts.types';
import { formatDate } from '../../../util/Date';
import { PageHeader } from '../../core/PageHeader';
import { PatientSelector } from '../../core/PatientSelector';
import { onPageDidMount, onPageWillUnmount } from '../../../redux/core';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/types';
import { useQueryString } from '../../../util/hooks';
import { InfoMessage } from '../../core/InfoMessage';
import { Response as CHResponse, BenefitsInformation, AdditionalInformation } from '../../../api/insurance.changehealthcare.types';

export const InsuranceToolChangeHealthcare = () => {
  const [insuranceId, setInsuranceId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [memberDateOfBirth, setMemberDateOfBirth] = useState<Date>();
  const [checking, setChecking] = useState(false);
  const [serviceTypeCode, setServiceTypeCode] = useState('30');
  const [response, setResponse] = useState<CHResponse | undefined>(undefined);
  const [error, setError] = useState<any>(undefined);

  const [patientId, setPatientId] = useQueryString('pid', '');
  const dispatch = useDispatch();
  const payers = useSelector((state: RootState) => state.visits.visitConfig.data?.payers || []);
  const payerList = payers.map((payer) => ({ text: payer.longName, key: payer.id }));

  const [view, setView] = useQueryString('view', 'plan');

  useEffect(() => {
    dispatch(onPageDidMount('InsuranceToolChangeHealthcare'));
    return () => {
      dispatch(onPageWillUnmount('InsuranceToolChangeHealthcare'));
    };
  }, [dispatch]);

  const onPatientChange = (patient: Patient) => {
    if (patient) {
      HealAPI.fetchAccountInfo(patient.ownerAccountId).then((response: ApiResponse<AccountResponse>) => {
        if (response.ok && response.data) {
          setInsuranceId(patient.insuranceId);
          setMemberId(patient.memberId);
          setMemberFirstName(patient.firstName);
          setMemberLastName(patient.lastName);
          setMemberDateOfBirth(new Date(patient.dateOfBirth));
          setPatientId(patient.patientId);
          setResponse(undefined);
        }
      });
    }
  };

  const canCheck = () => {
    if (insuranceId && memberId && memberDateOfBirth && !checking) {
      return true;
    }
    return false;
  };

  const mapInsuranceToChange = (insuranceId: string) => {
    const payer = payers.find((p) => p.id === insuranceId);
    const map: Record<string, string> = {
      '77023': '13285',
      '13285': '13285',
      '60054': 'AETNA',
      GABLS: 'GABCBS',
      NYBLS: 'EMHECH',
      RGBLS: 'WARBCS',
      HUMANA: 'HUMANA',
      '00431': 'CMSMED',
      '87726': 'UHC',
      '14163': 'WELLCR',
      '62308': 'CIGNA',
    };

    let code = 'unknown';
    if (payer) {
      code = map[payer.eligiblePayerCode] || 'unknown';
    }
    return code;
  };

  const check = () => {
    setChecking(true);
    setError(undefined);
    setResponse(undefined);
    changeCoverage({
      controlNumber: Math.floor(Math.random() * 1000000000)
        .toString()
        .padStart(9, '0'),
      tradingPartnerServiceId: mapInsuranceToChange(insuranceId),
      provider: {
        organizationName: 'Heal Doctors',
        npi: '1285017277',
      },
      subscriber: {
        firstName: memberFirstName,
        lastName: memberLastName,
        dateOfBirth: moment(memberDateOfBirth).format('YYYYMMDD'),
        memberId: memberId,
      },
      encounter: {
        dateOfService: moment().format('YYYYMMDD'),
        serviceTypeCodes: [serviceTypeCode],
      },
    })
      .then(setResponse)
      .catch(setError)
      .finally(() => setChecking(false));
  };

  return (
    <div>
      <PageHeader
        title="Insurance Eligibility Checker"
        showShareButton
        actions={{
          items: [
            {
              key: 'change',
              text: 'ChangeHealthcare...',
              iconProps: { iconName: 'NavigateExternalInline' },
              onClick: () => {
                window.open('https://connectcenter.changehealthcare.com/#/site/home', '_blank');
              },
            },
          ],
        }}
      />
      <InfoMessage>
        This tool queries ChangeHealthcare directly, you can select only one X12 Service Type Code. If only a single category of inquiry can be supported, use the Default [30]
        code.
      </InfoMessage>
      <br />
      <Stack horizontal tokens={{ childrenGap: 20 }}>
        <Stack disableShrink root={{ style: { minWidth: 300 } }} tokens={{ childrenGap: 8 }}>
          <PatientSelector label="Patient (search for an existing patient)" onChange={onPatientChange} patientId={patientId} />
          <div className={styles.divider} />
          <ComboBox
            label="Insurance Company"
            required
            selectedKey={insuranceId}
            allowFreeform
            autoComplete="on"
            onChange={(e, option) => option && setInsuranceId(option.key as string)}
            options={payerList}
            disabled={checking}
          />
          <ComboBox
            label="Service Type Code"
            required
            selectedKey={serviceTypeCode}
            allowFreeform
            autoComplete="on"
            onChange={(e, option) => option && setServiceTypeCode(option.key as string)}
            options={serviceCodeTypeOptions}
            disabled={checking}
          />
          <TextField label="Member ID" required onChange={(e, v) => setMemberId(v!)} value={memberId} disabled={checking} />
          <TextField label="First Name" onChange={(e, v) => setMemberFirstName(v!)} value={memberFirstName} disabled={checking} />
          <TextField label="Last Name" onChange={(e, v) => setMemberLastName(v!)} value={memberLastName} disabled={checking} />
          <DatePicker
            label="Date of Birth"
            isRequired
            onSelectDate={(v) => setMemberDateOfBirth(v!)}
            value={memberDateOfBirth}
            allowTextInput
            formatDate={formatDate}
            disabled={checking}
          />
          <PrimaryButton style={{ marginTop: 20, width: 180 }} text="Check" disabled={!canCheck()} onClick={() => check()} />
        </Stack>
        <Stack grow={1} style={{ width: '70%' }}>
          <Pivot onLinkClick={(i) => setView(i?.props.itemKey)} selectedKey={view}>
            <PivotItem headerText="Plan" itemKey="plan" />
            <PivotItem headerText="Json" itemKey="json" />
            <PivotItem headerText="X12" itemKey="x12" />
          </Pivot>
          <Stack className={styles.details}>
            {view === 'plan' && response && <Details data={response} serviceTypeCode={serviceTypeCode} />}
            {view === 'json' && <pre>{JSON.stringify(response, undefined, 2)}</pre>}
            {view === 'x12' && <TextField multiline rows={30} value={response?.x12} />}
            {checking && <Spinner size={SpinnerSize.large} />}
            {error && <pre>{JSON.stringify(error, undefined, 2)}</pre>}
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

// Co-insurance
function CodeA({ data }: { data: BenefitsInformation }) {
  return (
    <Stack className={styles.benefit}>
      <h4>
        {data.name} - {data.coverageLevel}
      </h4>
      <span>{data.serviceTypes}</span>
      <span>Percent: {parseFloat(data.benefitPercent || '') * 100} %</span>
      <span>In Network: {data.inPlanNetworkIndicator}</span>
      <AdditionalInfo info={data.additionalInformation} />
    </Stack>
  );
}

// Co-payment
function CodeB({ data }: { data: BenefitsInformation }) {
  return (
    <Stack className={styles.benefit}>
      <h4>
        {data.name} - {data.coverageLevel}
      </h4>
      <span>{data.serviceTypes}</span>
      <span>Amount: {data.benefitAmount}</span>
      <span>In Network: {data.inPlanNetworkIndicator}</span>
    </Stack>
  );
}

// Deductible
function CodeC({ data }: { data: BenefitsInformation }) {
  return (
    <Stack className={styles.benefit}>
      <h4>
        {data.name} - {data.coverageLevel}
      </h4>
      <span>{data.serviceTypes}</span>
      <span>Amount: {data.benefitAmount}</span>
      <AdditionalInfo info={data.additionalInformation} />
    </Stack>
  );
}

// Benefit Description
function CodeD({ data }: { data: BenefitsInformation }) {
  return (
    <Stack className={styles.benefit}>
      <h4>{data.name}</h4>
      <span>{data.serviceTypes}</span>
      <span>{data.inPlanNetworkIndicator}</span>
      <span>{data.planCoverage}</span>
      <AdditionalInfo info={data.additionalInformation} />
    </Stack>
  );
}

// Stop Loss
function CodeG({ data }: { data: BenefitsInformation }) {
  return (
    <Stack className={styles.benefit}>
      <h4>{data.name}</h4>
      <span>
        {data.timeQualifier} - {data.benefitAmount}
      </span>
      <AdditionalInfo info={data.additionalInformation} />
    </Stack>
  );
}

function AdditionalInfo({ info }: { info?: AdditionalInformation[] }) {
  return (
    <>
      {info?.length && <h5>Additional Notes:</h5>}
      {info?.map((ai) => (
        <span>{ai.description}</span>
      ))}
    </>
  );
}

function Details({ data, serviceTypeCode }: { data: CHResponse; serviceTypeCode: string }) {
  return (
    <Stack>
      <h3>Plan</h3>
      <span>{data.planInformation?.planDescription}</span>
      <span>{data.planInformation?.groupNumber}</span>
      <span>{data.planInformation?.groupDescription}</span>
      <span>{data.planDateInformation?.plan}</span>
      <span>
        {data.planDateInformation?.planBegin} - {data.planDateInformation?.planEnd}
      </span>

      <h3>Status</h3>
      <span>{data.planStatus && data.planStatus[0].status}</span>
      <span>{data.planStatus && data.planStatus[0].planDetails}</span>

      <h3>Benefits</h3>
      {data.benefitsInformation
        // ?.filter((b) => b.serviceTypeCodes?.includes(serviceTypeCode))
        ?.map((b, i) => {
          switch (b.code) {
            case 'A':
              return <CodeA data={b} />;
            case 'B':
              return <CodeB data={b} />;
            case 'C':
              return <CodeC data={b} />;
            case 'D':
              return <CodeD data={b} />;
            case 'G':
              return <CodeG data={b} />;
            default:
              return (
                <div key={i}>
                  <span>
                    {b.code} - {b.name}
                  </span>
                </div>
              );
          }
        })}
    </Stack>
  );
}

const serviceCodeTypeOptions = [
  { key: 'Header1', text: 'Commonly used', itemType: SelectableOptionMenuItemType.Header },
  { text: 'Default [30]', key: '30' },
  { text: 'Professional (Physician) [96]', key: '96' },
  { text: 'Professional (Physician) Visit - Home [A3]', key: 'A3' },
  { text: 'Professional (Physician) Visit - Inpatient [99]', key: '99' },
  { text: 'Professional (Physician) Visit - Nursing Home [A1]', key: 'A1' },
  { text: 'Professional (Physician) Visit - Office [98]', key: '98' },
  { text: 'Flu Vaccination [CO]', key: 'CO' },
  { text: 'Physician Visit - Office: Sick [BY]', key: 'BY' },
  { text: 'Physician Visit - Office: Well [BZ]', key: 'BZ' },
  { key: 'Header1', text: 'All', itemType: SelectableOptionMenuItemType.Header },
  { text: 'Abortion [84]', key: '84' },
  { text: 'Acupuncture [64]', key: '64' },
  { text: 'Adjunctive Dental Services [28]', key: '28' },
  { text: 'AIDS [85]', key: '85' },
  { text: 'Air Transportation [57]', key: '57' },
  { text: 'Alcoholism [AJ]', key: 'AJ' },
  { text: 'Allergy [GY]', key: 'GY' },
  { text: 'Allergy Testing [79]', key: '79' },
  { text: 'Alternate Method Dialysis [15]', key: '15' },
  { text: 'Ambulatory Service Center Facility [13]', key: '13' },
  { text: 'Anesthesia [7]', key: '7' },
  { text: 'Anesthesiologist [97]', key: '97' },
  { text: 'Audiology Exam [71]', key: '71' },
  { text: 'Blood Charges [10]', key: '10' },
  { text: 'Brand Name Prescription Drug [91]', key: '91' },
  { text: 'Brand Name Prescription Drug - Formulary [B2]', key: 'B2' },
  { text: 'Brand Name Prescription Drug - Non-Formulary [B3]', key: 'B3' },
  { text: 'Burn Care [B1]', key: 'B1' },
  { text: 'Cabulance [58]', key: '58' },
  { text: 'Cancer [87]', key: '87' },
  { text: 'Cardiac [BL]', key: 'BL' },
  { text: 'Cardiac Rehabilitation [BG]', key: 'BG' },
  { text: 'Case Management [CQ]', key: 'CQ' },
  { text: 'Chemotherapy [78]', key: '78' },
  { text: 'Chiropractic [33]', key: '33' },
  { text: 'Chiropractic Office Visits [34]', key: '34' },
  { text: 'Chronic Renal Disease (CRD) Equipment [16]', key: '16' },
  { text: 'Cognitive Therapy [BD]', key: 'BD' },
  { text: 'Consultation [3]', key: '3' },
  { text: 'Coronary Care [C1]', key: 'C1' },
  { text: 'Day Care (Psychiatric) [BC]', key: 'BC' },
  { text: 'Dental Accident [37]', key: '37' },
  { text: 'Dental Care [35]', key: '35' },
  { text: 'Dental Crowns [36]', key: '36' },
  { text: 'Dermatology [DG]', key: 'DG' },
  { text: 'Diabetic Supplies [DS]', key: 'DS' },
  { text: 'Diagnostic Dental [23]', key: '23' },
  { text: 'Diagnostic Lab [5]', key: '5' },
  { text: 'Diagnostic Medical [73]', key: '73' },
  { text: 'Diagnostic X-Ray [4]', key: '4' },
  { text: 'Dialysis [76]', key: '76' },
  { text: 'Donor Procedures [63]', key: '63' },
  { text: 'Drug Addiction [AK]', key: 'AK' },
  { text: 'Durable Medical Equipment [DM]', key: 'DM' },
  { text: 'Durable Medical Equipment Purchase [12]', key: '12' },
  { text: 'Durable Medical Equipment Rental [18]', key: '18' },
  { text: 'Emergency Services [86]', key: '86' },
  { text: 'Endocrine [BP]', key: 'BP' },
  { text: 'Endodontics [26]', key: '26' },
  { text: 'Experimental Drug Therapy [AR]', key: 'AR' },
  { text: 'Eye [BR]', key: 'BR' },
  { text: 'Eyewear and Eyewear Accessories [CP]', key: 'CP' },
  { text: 'Family Planning [82]', key: '82' },
  { text: 'Flu Vaccination [CO]', key: 'CO' },
  { text: 'Frames [AM]', key: 'AM' },
  { text: 'Free Standing Prescription Drug [89]', key: '89' },
  { text: 'Gastrointestinal [BN]', key: 'BN' },
  { text: 'General Benefits [60]', key: '60' },
  { text: 'Generic Prescription Drug [92]', key: '92' },
  { text: 'Generic Prescription Drug - Formulary [GF]', key: 'GF' },
  { text: 'Generic Prescription Drug - Non-Formulary [GN]', key: 'GN' },
  { text: 'Gynecological [BT]', key: 'BT' },
  { text: 'Home Health Care [42]', key: '42' },
  { text: 'Home Health Prescriptions [43]', key: '43' },
  { text: 'Home Health Visits [44]', key: '44' },
  { text: 'Hospice [45]', key: '45' },
  { text: 'Hospital [47]', key: '47' },
  { text: 'Hospital - Ambulatory Surgical [53]', key: '53' },
  { text: 'Hospital - Emergency Accident [51]', key: '51' },
  { text: 'Hospital - Emergency Medical [52]', key: '52' },
  { text: 'Hospital - Inpatient [48]', key: '48' },
  { text: 'Hospital - Outpatient [50]', key: '50' },
  { text: 'Hospital - Room and Board [49]', key: '49' },
  { text: 'Immunizations [80]', key: '80' },
  { text: 'In-vitro Fertilization [61]', key: '61' },
  { text: 'Independent Medical Evaluation [BA]', key: 'BA' },
  { text: 'Infertility [83]', key: '83' },
  { text: 'Inhalation Therapy [72]', key: '72' },
  { text: 'Intensive Care [IC]', key: 'IC' },
  { text: 'Invasive Procedures [BS]', key: 'BS' },
  { text: 'Lenses [AO]', key: 'AO' },
  { text: 'Licensed Ambulance [59]', key: '59' },
  { text: 'Long Term Care [54]', key: '54' },
  { text: 'Lymphatic [BM]', key: 'BM' },
  { text: 'Mail Order Prescription Drug [90]', key: '90' },
  { text: 'Mail Order Prescription Drug: Brand Name [BW]', key: 'BW' },
  { text: 'Mail Order Prescription Drug: Generic [BX]', key: 'BX' },
  { text: 'Major Medical [55]', key: '55' },
  { text: 'Mammogram, High Risk Patient [CM]', key: 'CM' },
  { text: 'Mammogram, Low Risk Patient [CN]', key: 'CN' },
  { text: 'Massage Therapy [BE]', key: 'BE' },
  { text: 'Maternity [69]', key: '69' },
  { text: 'Maxillofacial Prosthetics [27]', key: '27' },
  { text: 'Medical Care [1]', key: '1' },
  { text: 'Medically Related Transportation [56]', key: '56' },
  { text: 'Mental Health [MH]', key: 'MH' },
  { text: 'Mental Health Facility - Inpatient [CG]', key: 'CG' },
  { text: 'Mental Health Facility - Outpatient [CH]', key: 'CH' },
  { text: 'Mental Health Provider - Inpatient [CE]', key: 'CE' },
  { text: 'Mental Health Provider - Outpatient [CF]', key: 'CF' },
  { text: 'MRI/CAT Scan [62]', key: '62' },
  { text: 'Neonatal Intensive Care [NI]', key: 'NI' },
  { text: 'Neurology [BQ]', key: 'BQ' },
  { text: 'Newborn Care [65]', key: '65' },
  { text: 'Nonmedically Necessary Physical [AQ]', key: 'AQ' },
  { text: 'Nursery [BI]', key: 'BI' },
  { text: 'Obstetrical [BU]', key: 'BU' },
  { text: 'Obstetrical/Gynecological [BV]', key: 'BV' },
  { text: 'Occupational Therapy [AD]', key: 'AD' },
  { text: 'Oncology [ON]', key: 'ON' },
  { text: 'Oral Surgery [40]', key: '40' },
  { text: 'Orthodontics [38]', key: '38' },
  { text: 'Orthopedic [BK]', key: 'BK' },
  { text: 'Other Medical [9]', key: '9' },
  { text: 'Otological Exam [77]', key: '77' },
  { text: 'Partial Hospitalization (Psychiatric) [BB]', key: 'BB' },
  { text: 'Pathology [66]', key: '66' },
  { text: 'Pediatric [BH]', key: 'BH' },
  { text: 'Periodontics [24]', key: '24' },
  { text: 'Pharmacy [88]', key: '88' },
  { text: 'Physical Medicine [AE]', key: 'AE' },
  { text: 'Physical Therapy [PT]', key: 'PT' },
  { text: 'Physician Visit - Office: Sick [BY]', key: 'BY' },
  { text: 'Physician Visit - Office: Well [BZ]', key: 'BZ' },
  { text: 'Plan Waiting Period [32]', key: '32' },
  { text: 'Pneumonia Vaccine [19]', key: '19' },
  { text: 'Podiatry [93]', key: '93' },
  { text: 'Podiatry - Nursing Home Visits [95]', key: '95' },
  { text: 'Podiatry - Office Visits [94]', key: '94' },
  { text: 'Pre-Admission Testing [17]', key: '17' },
  { text: 'Private Duty Nursing [74]', key: '74' },
  { text: 'Private Duty Nursing - Home [CB]', key: 'CB' },
  { text: 'Private Duty Nursing - Inpatient [CA]', key: 'CA' },
  { text: 'Professional (Physician) [96]', key: '96' },
  { text: 'Professional (Physician) Visit - Home [A3]', key: 'A3' },
  { text: 'Professional (Physician) Visit - Inpatient [99]', key: '99' },
  { text: 'Professional (Physician) Visit - Nursing Home [A1]', key: 'A1' },
  { text: 'Professional (Physician) Visit - Office [98]', key: '98' },
  { text: 'Professional (Physician) Visit - Outpatient [A0]', key: 'A0' },
  { text: 'Professional (Physician) Visit - Skilled Nursing [A2]', key: 'A2' },
  { text: 'Prosthetic Device [75]', key: '75' },
  { text: 'Prosthodontics [39]', key: '39' },
  { text: 'Psychiatric [A4]', key: 'A4' },
  { text: 'Psychiatric - Inpatient [A7]', key: 'A7' },
  { text: 'Psychiatric - Outpatient [A8]', key: 'A8' },
  { text: 'Psychiatric - Room and Board [A5]', key: 'A5' },
  { text: 'Psychotherapy [A6]', key: 'A6' },
  { text: 'Pulmonary [PU]', key: 'PU' },
  { text: 'Pulmonary Rehabilitation [BF]', key: 'BF' },
  { text: 'Radiation Therapy [6]', key: '6' },
  { text: 'Rehabilitation [A9]', key: 'A9' },
  { text: 'Rehabilitation - Inpatient [AB]', key: 'AB' },
  { text: 'Rehabilitation - Outpatient [AC]', key: 'AC' },
  { text: 'Rehabilitation - Room and Board [AA]', key: 'AA' },
  { text: 'Renal [RN]', key: 'RN' },
  { text: 'Renal Supplies in the Home [14]', key: '14' },
  { text: 'Residential Psychiatric Treatment [RT]', key: 'RT' },
  { text: 'Respite Care [46]', key: '46' },
  { text: 'Restorative [25]', key: '25' },
  { text: 'Routine (Preventive) Dental [41]', key: '41' },
  { text: 'Routine Exam. Use for Routine Vision Exam only. [AN]', key: 'AN' },
  { text: 'Routine Physical [81]', key: '81' },
  { text: 'Screening laboratory [CL]', key: 'CL' },
  { text: 'Screening X-ray [CK]', key: 'CK' },
  { text: 'Second Surgical Opinion [20]', key: '20' },
  { text: 'Skilled Nursing Care [AG]', key: 'AG' },
  { text: 'Skilled Nursing Care - Room and Board [AH]', key: 'AH' },
  { text: 'Skin [BJ]', key: 'BJ' },
  { text: 'Smoking Cessation [67]', key: '67' },
  { text: 'Social Work [22]', key: '22' },
  { text: 'Speech Therapy [AF]', key: 'AF' },
  { text: 'Substance Abuse [AI]', key: 'AI' },
  { text: 'Substance Abuse Facility - Inpatient [CI]', key: 'CI' },
  { text: 'Substance Abuse Facility - Outpatient [CJ]', key: 'CJ' },
  { text: 'Surgical [2]', key: '2' },
  { text: 'Surgical Assistance [8]', key: '8' },
  { text: 'Surgical Benefits - Facility [CD]', key: 'CD' },
  { text: 'Surgical Benefits - Professional (Physician) [CC]', key: 'CC' },
  { text: 'Third Surgical Opinion [21]', key: '21' },
  { text: 'Transitional Care [TC]', key: 'TC' },
  { text: 'Transitional Nursery Care [TN]', key: 'TN' },
  { text: 'Transplants [70]', key: '70' },
  { text: 'Urgent Care [UC]', key: 'UC' },
  { text: 'Used Durable Medical Equipment [11]', key: '11' },
  { text: 'Vision (Optometry) [AL]', key: 'AL' },
  { text: 'Well Baby Care [68]', key: '68' },
];
